import {
  GlobalNavbarContext,
  GlobalNavbarRight,
  type SideNavDrawerProps,
} from "@dmca/frontend/components/nav";
import {
  authClient,
  GlobalContext,
  isSessionValid,
  SESSION_QUERY_KEY,
  useLocalStorage,
  userHasPermission,
  type ApplicationUser,
} from "@dmca/frontend/hooks";
import { IBetterAuthUserDBModelToIUserTransform } from "@dmca/models/interface/user";
import {
  Alert,
  AlertTitle,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
} from "@tanstack/react-router";
import { useRef, useEffect, useMemo } from "react";
import { parse } from "valibot";
import { RootLayout } from "../../components/RootLayout";
import type { AppPermissionIdentifierWithEffect } from "@dmca/models/interface/auth";
import { navItems, type LinkItem } from "../../nav-items";
import { useHelpNavSection } from "../../use-help-nav-section";
import { Link } from "@dmca/frontend/components/button";
import { setTag } from "@sentry/react";
import { HelpSections } from "@dmca/models/interface/help";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

export const Route = createFileRoute("/_authed")({
  component: RouteComponent,
  notFoundComponent: () => <div>hi i'm not found oops</div>,
  beforeLoad: async ({ location, context: { qc } }) => {
    const redirectToLogin = () => {
      return redirect({
        to: "/login",
        search: {
          callbackURL: location.pathname,
        },
      });
    };

    const sessionQuery = {
      queryKey: SESSION_QUERY_KEY,
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        return await authClient.getSession({ fetchOptions: { signal } });
      },
      staleTime: 1000 * 60 * 5, // 5 minutes
    };

    // try {
    let session = await qc.ensureQueryData({
      ...sessionQuery,
      revalidateIfStale: true,
    });

    if (!isSessionValid(session)) {
      // The cached copy may just be past its expiry while the server would
      // renew it — get a fresh session before deciding to bounce to login.
      qc.removeQueries({ queryKey: sessionQuery.queryKey });
      session = await qc.ensureQueryData(sessionQuery);
    }

    // The data check is redundant at runtime (isSessionValid covers it) but
    // narrows session.data for the code below.
    // A temporary auth API failure is not proof that the session expired.
    // Let the error page retry instead of stranding a valid user on /login
    if (session.error) {
      if (session.error.status === 401) {
        throw redirectToLogin();
      }
      throw session.error;
    }

    if (!isSessionValid(session) || !session.data?.user) {
      throw redirectToLogin();
    }

    const user: ApplicationUser = {
      ...parse(IBetterAuthUserDBModelToIUserTransform, session.data.user),
      email: session.data.user.email,
      phone: session.data.user.phone || undefined,
      accessToken: "",
      refreshToken: "",
      idToken: "",
    };

    setTag("app_user_id", user.id);

    // These are used by DefaultErrorComponent, if we made it here we likely don't have the error anymore
    sessionStorage.removeItem("error-reload-timer");
    sessionStorage.removeItem("uncaught-error-hash");

    return { user };
    // } catch (e) {
    //   console.error("Error in _authed beforeLoad:", e);
    //   // throw new Error("User not found");
    //   throw redirectToLogin();
    // }
  },
});

function RouteComponent() {
  const { trpcQcProxy, user } = Route.useRouteContext();
  const pathname = useLocation({ select: (loc) => loc.pathname });
  const cancelTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const [open, setOpen] = useLocalStorage("global-nav-is-open", isDesktop);

  const { data: tenantPermissions } = useQuery(
    trpcQcProxy.auth.getTenantPermissions.queryOptions(undefined, {
      // Prevents query from running until a user is available
      enabled: !!user,
    })
  );
  const { data: helpPages } = useQuery(
    trpcQcProxy.help.getPages.queryOptions(undefined, {
      enabled: !!user,
    })
  );

  const currentHelpSection = useMemo(() => {
    if (pathname.startsWith("/help")) return undefined;

    return HelpSections.find(
      (section) =>
        section.routes.includes(pathname) ||
        section.routePrefixes.some((prefix) => pathname.startsWith(prefix))
    );
  }, [pathname]);

  const currentHelpPageExists = currentHelpSection
    ? helpPages?.pages.some((page) => page.section === currentHelpSection.id)
    : false;
  const canManageCurrentHelpSection =
    !!currentHelpSection &&
    userHasPermission(
      currentHelpSection.managePermission,
      user,
      tenantPermissions ?? []
    );
  const showCurrentHelpButton =
    !!currentHelpSection &&
    (currentHelpPageExists || canManageCurrentHelpSection);

  const helpNavSection = useHelpNavSection();
  const navItemsWithHelp = useMemo(
    () => [...navItems, helpNavSection],
    [helpNavSection]
  );

  // Reload page if idle for > 10 minutes
  const reload = () => {
    if (cancelTimeoutRef.current) {
      clearTimeout(cancelTimeoutRef.current);
    }

    cancelTimeoutRef.current = setTimeout(() => {
      window.location.reload();
    }, 60000 * 5); // 10 minutes
  };

  const leftRef = useRef<HTMLDivElement | null>(null);
  const centerRef = useRef<HTMLDivElement | null>(null);
  const rightRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      const events = [
        "keypress",
        "mousemove",
        "mousedown",
        "touchstart",
        "touchmove",
        "touchend",
      ] as const;
      reload();
      events.forEach((event) => window.addEventListener(event, reload));

      return () => {
        if (cancelTimeoutRef.current) {
          clearTimeout(cancelTimeoutRef.current);
        }
        events.forEach((event) => window.removeEventListener(event, reload));
      };
    }
    return;
  }, []);

  return (
    <>
      <GlobalContext.Provider value={{ navIsOpen: [open, setOpen], isDesktop }}>
        <GlobalNavbarContext.Provider
          value={{
            center: centerRef,
            left: leftRef,
            right: rightRef,
          }}
        >
          {!user ? (
            <Outlet />
          ) : (
            <RootLayout
              isDesktop={isDesktop}
              navDrawerState={[open, setOpen]}
              navItems={generateSideNavRoutes(
                user,
                tenantPermissions ?? [],
                navItemsWithHelp
              )}
            >
              {showCurrentHelpButton && currentHelpSection && (
                <GlobalNavbarRight>
                  <Tooltip title={`${currentHelpSection.label} Help`}>
                    <IconButton
                      aria-label={`${currentHelpSection.label} Help`}
                      color="inherit"
                      component={Link}
                      to="/help/$section"
                      params={{ section: currentHelpSection.id }}
                    >
                      <HelpOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </GlobalNavbarRight>
              )}
              <Collapse
                in={
                  user.hasPhone &&
                  !user.phoneVerified &&
                  !pathname.includes("/settings/user")
                }
                sx={{ mb: 3 }}
                unmountOnExit
              >
                <Alert severity="warning">
                  <AlertTitle sx={{ fontWeight: 600 }}>
                    Phone Verification Required
                  </AlertTitle>
                  <Typography variant="body2">
                    Your phone number has not been verified.{" "}
                    <Link to="/settings/user/profile">
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                      >
                        Verify now
                      </Typography>
                    </Link>{" "}
                    to ensure you can receive important notifications.
                  </Typography>
                </Alert>
              </Collapse>
              <Outlet />
            </RootLayout>
          )}
        </GlobalNavbarContext.Provider>
      </GlobalContext.Provider>
    </>
  );
}

function recursivelyValidateSideNavRoutes(
  items: LinkItem[],
  user: ApplicationUser | undefined,
  tenantPermissions: AppPermissionIdentifierWithEffect[] = []
): LinkItem[] {
  // If no user is provided, return the items as-is
  if (!user) return items;

  return items
    .filter((item) => {
      // Check if user is authorized for this item
      if (item.permission) {
        const res = userHasPermission(item.permission, user, tenantPermissions);
        return res;
      }
      // Items without auth requirements are always visible
      return true;
    })
    .map((item) => {
      // Handle children recursively if present
      if (item.children && item.children.length > 0) {
        return {
          ...item,
          children: recursivelyValidateSideNavRoutes(
            item.children,
            user,
            tenantPermissions
          ),
        };
      }
      return item;
    })
    .filter((item) => {
      // Remove parent categories that have no visible children
      if (item.children) {
        return item.children.length > 0;
      }
      return true;
    });
}

function generateSideNavRoutes(
  user: ApplicationUser | undefined,
  tenantPermissions: AppPermissionIdentifierWithEffect[],
  items: LinkItem[]
): SideNavDrawerProps["items"] {
  if (!user) return [];

  const parsedItems = recursivelyValidateSideNavRoutes(
    items,
    user,
    tenantPermissions
  );
  return parsedItems;
}
